import {
  KPI_AGGREGATION_LABELS,
  targetPeriodLabel,
  TRAFFIC_LIGHT_LABELS,
  unitComparisonKpisSchema,
  unitComparisonSchema,
  type TrafficLight,
  type UnitComparison,
} from '@fluxus/shared';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Building2, FolderKanban } from 'lucide-react';
import { useId } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ReferenceLine,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts';
import { PageContainer, PageHeader } from '@/components/layout/PageHeader';
import { EmptyState, ErrorState, LoadingState } from '@/components/states/States';
import { StatusBadge } from '@/components/status/StatusBadge';
import { Button } from '@/components/ui/button';
import { NativeSelect } from '@/components/ui/select';
import { DRILL_PARAMS, drillHref, readId, readPeriod, type DrillPath } from '@/features/drill/drill';
import { DrillTrail } from '@/features/drill/DrillTrail';
import { UnitCause } from '@/features/drill/UnitCause';
import { ProjectPicker, useCurrentProject } from '@/features/workspace/workspace';
import { useAuth, useCurrentUser } from '@/lib/auth/session';

const formatValue = (v: number) => v.toLocaleString('pt-BR', { maximumFractionDigits: 2 });
const withUnit = (v: number | null, unit: string) =>
  v === null ? '—' : unit === 'R$' ? `R$ ${formatValue(v)}` : `${formatValue(v)} ${unit}`;
const percent = (v: number | null) => (v === null ? null : `${formatValue(v)}%`);
/** Cor da barra pelo semáforo (os mesmos tokens do selo). */
const BAR_FILL: Record<TrafficLight, string> = {
  green: 'var(--status-green-solid)',
  yellow: 'var(--status-yellow-solid)',
  red: 'var(--status-red-solid)',
  gray: 'var(--status-gray-solid)',
  blue: 'var(--status-blue-solid)',
};

/**
 * Indicadores › Por unidade (T-067, RF-53, DEC-037): o realizado, a meta, a performance e o semáforo de
 * cada unidade num mês (e no acumulado do ano), com o consolidado da empresa e um gráfico comparativo.
 */
export function PorUnidadePage() {
  const { organizationId = '' } = useParams();
  const user = useCurrentUser();
  const { workspace, projects, project, select } = useCurrentProject(organizationId);
  return (
    <PageContainer className="max-w-6xl">
      <PageHeader
        title="Por unidade"
        description="Compare as unidades e filiais num indicador: realizado, meta, performance e semáforo de cada uma, e o consolidado da empresa pela agregação do indicador."
      />
      {workspace.isPending ? (
        <LoadingState className="mt-6" rows={4} label="Carregando o projeto" />
      ) : workspace.isError ? (
        <ErrorState
          className="mt-6"
          error={workspace.error}
          onRetry={() => void workspace.refetch()}
          retrying={workspace.isFetching}
        />
      ) : !project ? (
        <EmptyState
          className="mt-6 rounded-lg border border-dashed border-border py-12"
          icon={FolderKanban}
          title="Nenhum projeto BSC em andamento"
          description="Os indicadores pertencem a um projeto. Quando a empresa tiver um projeto em andamento, eles aparecem aqui."
          action={
            user.organization.isFluxus ? (
              <Button asChild variant="outline">
                <Link to={`/empresas/${organizationId}`}>Abrir um projeto na página da empresa</Link>
              </Button>
            ) : null
          }
        />
      ) : (
        <div key={project.id} className="mt-4 flex flex-col gap-4">
          <ProjectPicker projects={projects} project={project} onSelect={select} />
          <ComparisonSection
            organizationId={organizationId}
            projectId={project.id}
            horizon={[project.horizonStart, project.horizonEnd]}
          />
        </div>
      )}
    </PageContainer>
  );
}

function ComparisonSection({
  organizationId,
  projectId,
  horizon,
}: {
  organizationId: string;
  projectId: string;
  horizon: [number, number];
}) {
  const { client } = useAuth();
  const base = `/organizations/${organizationId}/projects/${projectId}/results/by-unit`;
  // Indicador, unidade, período e o objetivo de onde se veio ficam no endereço (drill-down — T-144b, DEC-058).
  const [params, setParams] = useSearchParams();
  const now = new Date();
  const fallbackYear = Math.min(Math.max(now.getFullYear(), horizon[0]), horizon[1]);
  const asked = readPeriod(params);
  const year = asked && asked.year >= horizon[0] && asked.year <= horizon[1] ? asked.year : fallbackYear;
  const month =
    asked && asked.year === year ? asked.month : year === now.getFullYear() ? now.getMonth() + 1 : 12;
  const requestedKpi = readId(params, DRILL_PARAMS.kpi);
  const unitId = readId(params, DRILL_PARAMS.unit);
  const objectiveId = readId(params, DRILL_PARAMS.objective);
  const ids = { kpi: useId(), year: useId(), month: useId() };
  const years = Array.from({ length: horizon[1] - horizon[0] + 1 }, (_, i) => horizon[0] + i);
  const update = (changes: Record<string, string | null>) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        for (const [key, value] of Object.entries(changes)) {
          if (value) next.set(key, value);
          else next.delete(key);
        }
        return next;
      },
      { replace: true },
    );
  const setPeriod = (y: number, m: number) =>
    update({ [DRILL_PARAMS.year]: String(y), [DRILL_PARAMS.month]: String(m) });

  const kpis = useQuery({
    queryKey: ['by-unit', projectId, 'kpis'],
    // Os arquivados também (o histórico continua consultável, RN-05; o drill-down chega a eles pelo histórico).
    queryFn: ({ signal }) =>
      client.request(`${base}/kpis`, {
        query: { archived: 'include' },
        schema: unitComparisonKpisSchema,
        signal,
      }),
  });
  const options = [...(kpis.data?.items ?? [])].sort((a, b) => Number(a.archived) - Number(b.archived));
  // Indicador do link que não está nas opções (removido ou fora do acesso): avisa em vez de trocar de indicador.
  const missing = Boolean(requestedKpi) && Boolean(kpis.data) && !options.some((k) => k.id === requestedKpi);
  const chosen = missing ? '' : (requestedKpi ?? options[0]?.id ?? '');
  const comparison = useQuery({
    queryKey: ['by-unit', projectId, chosen, year],
    queryFn: ({ signal }) =>
      client.request(base, {
        query: { kpiId: chosen, year: String(year) },
        schema: unitComparisonSchema,
        signal,
      }),
    // Só depois da lista: o indicador do link precisa estar nela.
    enabled: Boolean(chosen) && kpis.isSuccess,
    placeholderData: keepPreviousData,
  });

  if (kpis.isPending) return <LoadingState rows={4} label="Carregando os indicadores" />;
  if (kpis.isError) {
    return <ErrorState error={kpis.error} onRetry={() => void kpis.refetch()} retrying={kpis.isFetching} />;
  }
  if (!options.length) {
    return (
      <EmptyState
        className="rounded-lg border border-dashed border-border py-12"
        icon={Building2}
        title="Nenhum indicador para comparar"
        description="Os indicadores das suas áreas (ou lançados pelas suas unidades) aparecem aqui para a comparação por unidade."
      />
    );
  }
  // O caminho só segue com o objetivo do link quando o indicador é o do link.
  const path: DrillPath = {
    projectId,
    kpiId: chosen,
    objectiveId: chosen === requestedKpi ? objectiveId : null,
    year,
    month,
  };

  return (
    <div className="flex flex-col gap-5">
      {chosen ? (
        <DrillTrail organizationId={organizationId} path={{ ...path, businessUnitId: unitId }} />
      ) : null}
      <div className="grid gap-3 sm:grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,1fr)]">
        <div>
          <label htmlFor={ids.kpi} className="mb-1 block text-xs font-medium text-muted-foreground">
            Indicador
          </label>
          <NativeSelect
            id={ids.kpi}
            value={chosen}
            // Outro indicador: a unidade e o objetivo do caminho anterior saem.
            onChange={(e) =>
              update({
                [DRILL_PARAMS.kpi]: e.target.value,
                [DRILL_PARAMS.unit]: null,
                [DRILL_PARAMS.objective]: null,
              })
            }
          >
            {missing ? <option value="">Escolha um indicador</option> : null}
            {options.map((k) => (
              <option key={k.id} value={k.id}>
                {k.code} — {k.name}
                {k.archived ? ' (arquivado)' : ''}
              </option>
            ))}
          </NativeSelect>
        </div>
        <div>
          <label htmlFor={ids.year} className="mb-1 block text-xs font-medium text-muted-foreground">
            Ano
          </label>
          <NativeSelect
            id={ids.year}
            value={String(year)}
            onChange={(e) => setPeriod(Number(e.target.value), month)}
          >
            {years.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </NativeSelect>
        </div>
        <div>
          <label htmlFor={ids.month} className="mb-1 block text-xs font-medium text-muted-foreground">
            Mês
          </label>
          <NativeSelect
            id={ids.month}
            value={String(month)}
            onChange={(e) => setPeriod(year, Number(e.target.value))}
          >
            {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
              <option key={m} value={m}>
                {targetPeriodLabel('MONTH', m)}
              </option>
            ))}
          </NativeSelect>
        </div>
      </div>

      {missing ? (
        <EmptyState
          className="rounded-lg border border-dashed border-border py-12"
          icon={Building2}
          title="Indicador não disponível"
          description="O indicador do link não existe neste projeto ou não está no seu acesso. Escolha outro na lista acima."
        />
      ) : comparison.isPending ? (
        <LoadingState rows={4} label="Carregando as unidades" />
      ) : comparison.isError ? (
        <ErrorState
          error={comparison.error}
          onRetry={() => void comparison.refetch()}
          retrying={comparison.isFetching}
        />
      ) : (
        <ComparisonView
          data={comparison.data}
          month={month}
          busy={comparison.isFetching}
          selectedUnit={unitId}
          unitHref={(id) => drillHref.unit(organizationId, { ...path, businessUnitId: id })}
        />
      )}
      {unitId && chosen ? (
        <div className="flex flex-col gap-2">
          <div className="flex justify-end">
            <Button variant="ghost" size="sm" onClick={() => update({ [DRILL_PARAMS.unit]: null })}>
              Fechar a unidade
            </Button>
          </div>
          <UnitCause
            organizationId={organizationId}
            projectId={projectId}
            kpiId={chosen}
            businessUnitId={unitId}
            year={year}
            month={month}
          />
        </div>
      ) : null}
    </div>
  );
}

function ComparisonView({
  data,
  month,
  busy,
  selectedUnit,
  unitHref,
}: {
  data: UnitComparison;
  month: number;
  busy: boolean;
  selectedUnit: string | null;
  /** Cada unidade leva à causa nela (drill-down). */
  unitHref: (unitId: string) => string;
}) {
  const label = `${targetPeriodLabel('MONTH', month)}/${data.year}`;
  const rows = [
    ...data.units.map((u) => ({
      key: u.id,
      name: `${u.name}${u.archived ? ' (arquivada)' : ''}`,
      weight: u.weight,
      point: u.months[month - 1]!,
      consolidated: false,
    })),
    ...(data.consolidated
      ? [
          {
            key: 'consolidado',
            name: 'Consolidado',
            weight: null,
            point: data.consolidated[month - 1]!,
            consolidated: true,
          },
        ]
      : []),
  ];
  const weighted = data.kpi.aggregation === 'WEIGHTED_AVERAGE';
  const chart = rows.map((r) => ({ name: r.name, performance: r.point.performance, light: r.point.light }));
  const withPerformance = chart.filter((c) => c.performance !== null);

  if (!data.units.length) {
    return (
      <EmptyState
        className="rounded-lg border border-dashed border-border py-12"
        icon={Building2}
        title="Nenhuma unidade cadastrada"
        description="A comparação aparece quando a empresa tem unidades ou filiais com realizado e meta lançados por unidade."
      />
    );
  }

  return (
    <div className="flex flex-col gap-5" aria-busy={busy}>
      <p className="text-sm text-muted-foreground">
        {data.access === 'AREA'
          ? `Consolidado pela agregação do indicador: ${KPI_AGGREGATION_LABELS[data.kpi.aggregation].toLowerCase()}${
              weighted ? ' (pelo peso de cada unidade)' : ''
            }. Com o realizado geral do mês, vale o geral.`
          : 'Você vê o indicador pelas suas unidades; o consolidado da empresa fica com quem acompanha a área dele.'}
      </p>
      <div
        role="region"
        aria-label={`Unidades em ${label}`}
        tabIndex={0}
        className="overflow-x-auto rounded-lg border border-border"
      >
        <table className="w-full min-w-[44rem] border-collapse text-sm">
          <caption className="sr-only">
            {data.kpi.name} por unidade em {label}
          </caption>
          <thead className="bg-muted/60 text-xs text-muted-foreground">
            <tr>
              <th scope="col" className="px-3 py-2 text-left font-semibold">
                Unidade
              </th>
              {weighted ? (
                <th scope="col" className="px-3 py-2 text-right font-semibold">
                  Peso
                </th>
              ) : null}
              <th scope="col" className="px-3 py-2 text-right font-semibold">
                Realizado
              </th>
              <th scope="col" className="px-3 py-2 text-right font-semibold">
                Meta
              </th>
              <th scope="col" className="px-3 py-2 text-left font-semibold">
                Performance do mês
              </th>
              <th scope="col" className="px-3 py-2 text-right font-semibold">
                Acumulado
              </th>
              <th scope="col" className="px-3 py-2 text-left font-semibold">
                Performance acumulada
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr
                key={r.key}
                className={
                  r.consolidated
                    ? 'border-t-2 border-border-strong bg-muted/40 font-semibold'
                    : 'border-t border-border'
                }
              >
                <th scope="row" className="px-3 py-2 text-left font-[inherit]">
                  {r.consolidated ? (
                    r.name
                  ) : (
                    <Link
                      to={unitHref(r.key)}
                      aria-current={selectedUnit === r.key ? 'true' : undefined}
                      className="text-brand-strong underline-offset-2 hover:underline aria-[current=true]:font-bold dark:text-brand"
                    >
                      {r.name}
                    </Link>
                  )}
                </th>
                {weighted ? (
                  <td className="px-3 py-2 text-right tabular-nums">
                    {r.weight === null ? '—' : formatValue(r.weight)}
                  </td>
                ) : null}
                <td className="px-3 py-2 text-right tabular-nums">
                  {withUnit(r.point.actual, data.kpi.unitLabel)}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {withUnit(r.point.target, data.kpi.unitLabel)}
                </td>
                <td className="px-3 py-2">
                  <StatusBadge
                    status={r.point.light}
                    label={percent(r.point.performance) ?? TRAFFIC_LIGHT_LABELS[r.point.light]}
                  />
                </td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {withUnit(r.point.ytdActual, data.kpi.unitLabel)}
                </td>
                <td className="px-3 py-2">
                  <StatusBadge
                    status={r.point.ytdLight}
                    label={percent(r.point.ytdPerformance) ?? TRAFFIC_LIGHT_LABELS[r.point.ytdLight]}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <section
        aria-label={`Performance por unidade em ${label}`}
        className="rounded-lg border border-border bg-surface p-4"
      >
        <h2 className="text-sm font-semibold">Performance por unidade em {label}</h2>
        {withPerformance.length ? (
          <>
            <div className="mt-3 h-64 w-full" role="img" aria-label={chartSummary(chart)}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chart} margin={{ top: 8, right: 8, bottom: 8, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 12, fill: 'var(--muted-foreground)' }}
                    interval={0}
                  />
                  <YAxis
                    unit="%"
                    width={48}
                    tick={{ fontSize: 12, fill: 'var(--muted-foreground)' }}
                    domain={[0, (max: number) => Math.max(120, Math.ceil(max / 10) * 10)]}
                  />
                  <ReferenceLine y={100} stroke="var(--border-strong)" strokeDasharray="4 4" />
                  <Bar dataKey="performance" radius={[4, 4, 0, 0]} isAnimationActive={false}>
                    {chart.map((c) => (
                      <Cell key={c.name} fill={BAR_FILL[c.light]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <p className="mt-2 text-xs text-muted-foreground">A linha tracejada marca 100% da meta.</p>
          </>
        ) : (
          <p className="mt-2 text-sm text-muted-foreground">
            Sem performance calculada neste mês (falta realizado ou meta).
          </p>
        )}
      </section>
    </div>
  );
}

/** Descrição do gráfico para leitores de tela (os mesmos números da tabela). */
function chartSummary(chart: { name: string; performance: number | null; light: TrafficLight }[]) {
  return `Gráfico de barras da performance: ${chart
    .map(
      (c) =>
        `${c.name} ${c.performance === null ? 'sem dados' : `${formatValue(c.performance)}% (${TRAFFIC_LIGHT_LABELS[c.light]})`}`,
    )
    .join('; ')}.`;
}
